import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import { Box, Skeleton, Alert, Typography, Button, CircularProgress } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios from 'axios';
import { withTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import useCalendarState from '../hooks/useCalendarState';
import {
  format, addDays, addMonths, startOfMonth, isSameDay, parseDateKey, dayGridHours, eventRowsPeriod, parseTimeToHour,
} from '../hooks/dateUtils';
import { weekSpanText } from '../hooks/monthUtils';
import { hoursForDate } from '../../../utils/weekdayHours';
import { useUserStore } from 'store';
import MobileCalendarHeader from './MobileCalendarHeader';
import MobileDayView from './MobileDayView';
import MobileMultiColumnView from './MobileMultiColumnView';
import MobileWeekDaySelector from './MobileWeekDaySelector';
import MobileMonthView from './MobileMonthView';
import MobileScheduleDialog from './MobileScheduleDialog';
import CalendarNoteDialog from '../CalendarNoteDialog';
import UnavailabilityDialog from '../UnavailabilityDialog';
import GoogleCalendarAuthBanner from '../GoogleCalendarAuthBanner';
import MobileCalendarSearchDialog from './MobileCalendarSearchDialog';

// The phone remembers day vs. month per organization, like the desktop remembers its mode.
const MOBILE_MODE_KEY_PREFIX = 'calendar_mobile_mode_';

function loadMobileMode(orgId) {
  try {
    const saved = orgId ? localStorage.getItem(`${MOBILE_MODE_KEY_PREFIX}${orgId}`) : null;
    return saved === 'month' ? 'month' : 'day';
  } catch (e) {
    return 'day';
  }
}

function saveMobileMode(orgId, mode) {
  try {
    if (orgId) localStorage.setItem(`${MOBILE_MODE_KEY_PREFIX}${orgId}`, mode);
  } catch (e) {
    // Private mode or blocked storage: the choice just does not persist.
  }
}

// The month view's "does an N h job fit" threshold, remembered per organization like the
// desktop's (its own key: the phone never reads the desktop preference blob).
const MOBILE_OPEN_HOURS_KEY_PREFIX = 'calendar_mobile_open_hours_';

function loadMobileOpenHours(orgId) {
  try {
    const saved = orgId ? parseFloat(localStorage.getItem(`${MOBILE_OPEN_HOURS_KEY_PREFIX}${orgId}`)) : NaN;
    return Number.isFinite(saved) && saved > 0 ? saved : null;
  } catch (e) {
    return null;
  }
}

function saveMobileOpenHours(orgId, hours) {
  try {
    if (!orgId) return;
    if (hours) localStorage.setItem(`${MOBILE_OPEN_HOURS_KEY_PREFIX}${orgId}`, String(hours));
    else localStorage.removeItem(`${MOBILE_OPEN_HOURS_KEY_PREFIX}${orgId}`);
  } catch (e) {
    // Private mode or blocked storage: the choice just does not persist.
  }
}

function MobileCalendarView({ t }) {
  const {
    loading,
    error,
    setLoading,
    setError,
    setCalendarData,
    setUnscheduledData,
    employees,
    serviceBays,
    events,
    unavailabilities,
    calendarNotes,
    unscheduledTotalCount,
    workSchedule,
    openEditNoteDialog,
    openNoteDialogForSlot,
    openUnavailabilityDialogForSlot,
    openEditUnavailabilityDialog,
    setHighlightedMaintenance,
    clearHighlightedMaintenance,
  } = useCalendarState();

  const { currentOrganization } = useUserStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);

  // Mobile-specific state
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedResourceId, setSelectedResourceId] = useState(null);
  const [selectedResourceType, setSelectedResourceType] = useState('employee'); // 'employee' or 'bay'
  const [refreshKey, setRefreshKey] = useState(0);

  // The day timeline's rows: the shown day's work hours (a phone view shows one day, so the rest
  // of the week's longer days would only be empty rows), widened to fit that day's bookings and
  // the mechanics' own hours that day. Outside the hours is hatched.
  const selectedDateKey = format(selectedDate, 'yyyy-MM-dd');
  const rowPeriods = events.map((event) => eventRowsPeriod(event, selectedDateKey));
  if (selectedResourceType === 'employee') {
    employees.forEach((emp) => {
      const own = emp.work_hours ? hoursForDate(emp.work_hours, selectedDate) : null;
      if (own) rowPeriods.push({ start: parseTimeToHour(own.start), end: parseTimeToHour(own.end) });
    });
  }
  const { startHour, endHour } = dayGridHours({ workSchedule, date: selectedDate, periods: rowPeriods });

  // Month mode: its own cursor so flipping months does not move the selected day, and its
  // own payload from /api/scheduling/calendar/month/.
  const [mobileMode, setMobileMode] = useState(() => loadMobileMode(currentOrganization?.organization_id));
  const [monthCursor, setMonthCursor] = useState(() => startOfMonth(new Date()));
  const [monthData, setMonthData] = useState(null);
  const [openHours, setOpenHours] = useState(() => loadMobileOpenHours(currentOrganization?.organization_id));
  const [monthLoading, setMonthLoading] = useState(false);
  // A failed month fetch is shown in place of the month grid, not as the full-page error:
  // the mode is remembered, so the header with the mode switch has to stay reachable.
  const [monthError, setMonthError] = useState(false);
  const isMonthMode = mobileMode === 'month';
  // A ?mode= deep link beats the remembered mode when the organization loads after mount
  const urlModeApplied = useRef(false);

  useEffect(() => {
    if (currentOrganization && !urlModeApplied.current) {
      setMobileMode(loadMobileMode(currentOrganization.organization_id));
    }
  }, [currentOrganization]);

  // Handle URL params on mount (for navigation from maintenance detail)
  useEffect(() => {
    const dateParam = searchParams.get('date');
    const viewParam = searchParams.get('view');
    const modeParam = searchParams.get('mode');

    if (dateParam) {
      setSelectedDate(new Date(dateParam));
      setMonthCursor(startOfMonth(new Date(dateParam)));
      searchParams.delete('date');
    }

    // ?mode=month opens the month grid; day and week both mean the day view on a phone
    if (modeParam) {
      const mode = modeParam === 'month' ? 'month' : 'day';
      urlModeApplied.current = true;
      setMobileMode(mode);
      saveMobileMode(currentOrganization?.organization_id, mode);
      searchParams.delete('mode');
    } else if (dateParam && searchParams.get('highlight')) {
      // A link to one work order's day (from the work order page): the month grid cannot
      // show it, so this visit opens the day. The remembered mode is left as it is.
      urlModeApplied.current = true;
      setMobileMode('day');
    }

    // Set view mode if specified in URL
    if (viewParam === 'all') {
      setSelectedResourceType('all');
      setSelectedResourceId(null);
      searchParams.delete('view');
    }

    // Clean highlight param (not used in mobile but clean it anyway)
    searchParams.delete('highlight');

    if (dateParam || viewParam || modeParam) {
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Month payload: refetched when the month, the organization or a refresh changes.
  const monthKey = format(monthCursor, 'yyyy-MM');
  useEffect(() => {
    if (!currentOrganization || !isMonthMode) return undefined;
    let cancelled = false;
    setMonthLoading(true);
    setMonthError(false);
    const params = { month: monthKey };
    if (openHours) params.open_hours = openHours;
    axios
      .get('/api/scheduling/calendar/month/', { params })
      .then((response) => {
        if (!cancelled) setMonthData(response.data);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Error fetching month data:', err);
        setMonthError(true);
      })
      .finally(() => {
        if (!cancelled) setMonthLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthKey, isMonthMode, refreshKey, currentOrganization, openHours]);

  // Dialog state for mobile
  const [mobileDialogOpen, setMobileDialogOpen] = useState(false);
  const [mobileDialogMode, setMobileDialogMode] = useState('create'); // 'create' or 'edit'
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Set initial resource type to 'employee' when data loads (default to all employees)
  useEffect(() => {
    if (employees.length > 0 && selectedResourceType === 'employee' && selectedResourceId === undefined) {
      setSelectedResourceId(null);
    }
  }, [employees, selectedResourceType, selectedResourceId]);

  // Fetch calendar data
  useEffect(() => {
    if (!currentOrganization) return;

    // A deep link (?date=) changes the date right after mount, so two windows can be in
    // flight at once. Only the latest effect may write to the shared store.
    let cancelled = false;

    const fetchCalendarData = async () => {
      setLoading(true);
      setError(null);

      try {
        // For mobile, we fetch a 7-day window around the selected date
        const startDate = addDays(selectedDate, -3);
        const endDate = addDays(selectedDate, 3);

        const calendarResponse = await axios.get('/api/scheduling/calendar/', {
          params: {
            start: format(startDate, 'yyyy-MM-dd'),
            end: format(endDate, 'yyyy-MM-dd'),
            // Use 'employee' view for API when in 'all' mode to get all events
            view: selectedResourceType === 'all' ? 'employee' : selectedResourceType,
          },
        });

        if (cancelled) return;
        setCalendarData(calendarResponse.data);
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching calendar data:', err);
        setError(err.response?.data?.detail || err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchCalendarData();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, selectedResourceType, refreshKey, currentOrganization]);

  // Fetch the unscheduled list separately — it does not depend on the visible
  // date range, so it should only refetch on org change or explicit refresh.
  useEffect(() => {
    if (!currentOrganization) return;

    const fetchUnscheduled = async () => {
      try {
        const unscheduledResponse = await axios.get('/api/scheduling/calendar/unscheduled/', {
          params: { limit: 50 },
        });

        const unscheduledData = unscheduledResponse.data;
        setUnscheduledData(unscheduledData.results, unscheduledData.total_count, unscheduledData.has_more);
      } catch (err) {
        console.error('Error fetching unscheduled maintenances:', err);
      }
    };

    fetchUnscheduled();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey, currentOrganization]);

  const handleRefresh = () => {
    setRefreshKey((k) => k + 1);
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setMonthCursor(startOfMonth(date));
  };

  const handleModeChange = (mode) => {
    setMobileMode(mode);
    saveMobileMode(currentOrganization?.organization_id, mode);
    if (mode === 'month') {
      setMonthCursor(startOfMonth(selectedDate));
    }
  };

  const handlePrevMonth = () => setMonthCursor(addMonths(monthCursor, -1));
  const handleNextMonth = () => setMonthCursor(addMonths(monthCursor, 1));

  // A tapped month cell opens that day in the day view
  const handleMonthDaySelect = (day) => {
    const date = parseDateKey(day.date);
    if (!date) return;
    setSelectedDate(date);
    handleModeChange('day');
  };

  const handleJumpToNextFreeDay = () => {
    const target = monthData?.next_free_day ? parseDateKey(monthData.next_free_day) : null;
    if (!target) return;
    setSelectedDate(target);
    setMonthCursor(startOfMonth(target));
  };

  // null restores the server's default rule (16 h).
  const handleOpenHoursChange = (hours) => {
    const value = hours > 0 ? hours : null;
    setOpenHours(value);
    saveMobileOpenHours(currentOrganization?.organization_id, value);
  };

  const handleResourceChange = (resourceId, resourceType) => {
    setSelectedResourceId(resourceId);
    setSelectedResourceType(resourceType);
  };

  const handlePrevDay = () => {
    setSelectedDate(addDays(selectedDate, -1));
  };

  const handleNextDay = () => {
    setSelectedDate(addDays(selectedDate, 1));
  };

  const handleToday = () => {
    const today = new Date();
    setSelectedDate(today);
    setMonthCursor(startOfMonth(today));
  };

  // Filter events for the selected day and resource, with multi-day event support
  // Mirrors desktop CalendarGrid.js multi-day handling (clamped hours per day)
  const getEventsForDay = () => {
    const selectedDateStr = format(selectedDate, 'yyyy-MM-dd');

    return events.reduce((result, event) => {
      // Resource filter — skip when resourceId is null (all within that mode)
      if (selectedResourceType !== 'all' && selectedResourceId) {
        if (selectedResourceType === 'employee') {
          if (!event.employee_ids?.includes(selectedResourceId)) return result;
        } else {
          if (event.service_bay_id !== selectedResourceId) return result;
        }
      }

      const isMultiDay = event.local_start_date && event.local_end_date &&
        event.local_start_date !== event.local_end_date;

      if (isMultiDay) {
        // Check if selected day falls within event's date range
        if (selectedDateStr >= event.local_start_date && selectedDateStr <= event.local_end_date) {
          const startsToday = event.local_start_date === selectedDateStr;
          const endsToday = event.local_end_date === selectedDateStr;

          const clampedEvent = {
            ...event,
            _clampedStartHour: startsToday ? Math.max(startHour, event.local_start_hour) : startHour,
            _clampedEndHour: endsToday ? Math.min(endHour, event.local_end_hour) : endHour,
            _isMultiDay: true,
            _dayPosition: startsToday ? 'first' : (endsToday ? 'last' : 'middle'),
          };

          if (clampedEvent._clampedEndHour > clampedEvent._clampedStartHour) {
            result.push(clampedEvent);
          }
        }
      } else {
        // Single-day: use original isSameDay check
        const eventDate = new Date(event.start_time);
        if (isSameDay(eventDate, selectedDate)) {
          result.push(event);
        }
      }

      return result;
    }, []);
  };

  // Filter unavailabilities for the selected day and resource (only for employees)
  const getUnavailabilitiesForDay = () => {
    if (selectedResourceType !== 'employee') return [];

    return unavailabilities.filter((unavail) => {
      const startDate = new Date(unavail.start_time);
      const endDate = new Date(unavail.end_time);

      // Check if unavailability overlaps with selected date
      const dayStart = new Date(selectedDate);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(selectedDate);
      dayEnd.setHours(23, 59, 59, 999);

      const overlaps = startDate <= dayEnd && endDate >= dayStart;
      const matchesEmployee = unavail.employee_id === selectedResourceId;

      return overlaps && matchesEmployee;
    });
  };

  // Notes for the selected day: every note in the combined view, one mechanic's notes in
  // their day view, and with all mechanics side by side every pinned note (the column view
  // hands each one to its mechanic's column; whole-workshop notes live in the combined view).
  const getCalendarNotesForDay = () => {
    if (selectedResourceType !== 'all' && selectedResourceType !== 'employee') return [];

    return calendarNotes.filter((note) => {
      if (selectedResourceType === 'employee') {
        if (selectedResourceId ? note.employee_id !== selectedResourceId : !note.employee_id) {
          return false;
        }
      }

      const startDate = new Date(note.start_time);
      const endDate = new Date(note.end_time);

      const dayStart = new Date(selectedDate);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(selectedDate);
      dayEnd.setHours(23, 59, 59, 999);

      return startDate <= dayEnd && endDate >= dayStart;
    });
  };

  // Jump the day view to a search hit. Work orders with no slot have nowhere to jump to
  // on mobile (there is no unscheduled sidebar), so open the work order instead.
  const handleSearchSelect = (result) => {
    setSearchDialogOpen(false);

    if (!result.is_scheduled || !result.local_start_date) {
      navigate(`/maintenances/${result.maintenance_id}`);
      return;
    }

    const [year, month, day] = result.local_start_date.split('-').map(Number);
    if (year) {
      setSelectedDate(new Date(year, month - 1, day));
    }
    setHighlightedMaintenance(result.maintenance_id);
    setTimeout(() => clearHighlightedMaintenance(), 5000);
  };

  const handleTimeSlotTap = (hour, resourceId = null) => {
    setSelectedTimeSlot({
      date: selectedDate,
      startHour: hour,
      endHour: hour + 1,
      resourceId: resourceId || selectedResourceId,
      resourceType: resourceId ? selectedResourceType : selectedResourceType,
    });
    setSelectedEvent(null);
    setMobileDialogMode('create');
    setMobileDialogOpen(true);
  };

  const handleEventTap = (event) => {
    // Calendar notes and absences open their own dialogs
    if (event._itemType === 'calendar_note') {
      openEditNoteDialog(event);
      return;
    }
    if (event._itemType === 'unavailability') {
      openEditUnavailabilityDialog(event);
      return;
    }
    setSelectedEvent(event);
    setSelectedTimeSlot(null);
    setMobileDialogMode('edit');
    setMobileDialogOpen(true);
  };

  const handleDialogClose = () => {
    setMobileDialogOpen(false);
    setSelectedTimeSlot(null);
    setSelectedEvent(null);
  };

  // "Add note" inside the booking dialog: the tapped hour becomes the note's span. A tap in
  // a mechanic's column pins the note to that mechanic; the combined view writes one for
  // the whole workshop. A bay column has no notes, so the dialog gets no handler there.
  const handleAddNote = () => {
    if (!selectedTimeSlot) return;
    const slot = {
      date: selectedTimeSlot.date,
      startHour: selectedTimeSlot.startHour,
      endHour: selectedTimeSlot.endHour,
    };
    if (selectedTimeSlot.resourceType === 'employee' && selectedTimeSlot.resourceId) {
      slot.resourceType = 'employee';
      slot.resourceId = selectedTimeSlot.resourceId;
    }
    handleDialogClose();
    openNoteDialogForSlot(slot);
  };

  // "Mark as unavailable" inside the booking dialog: only from a mechanic's column or their
  // own day view (the desktop offers it only in the employee view), so the absence has its
  // employee and the tapped hour from the start.
  const handleAddUnavailability = () => {
    if (!selectedTimeSlot || selectedTimeSlot.resourceType !== 'employee' || !selectedTimeSlot.resourceId) return;
    const slot = {
      date: selectedTimeSlot.date,
      startHour: selectedTimeSlot.startHour,
      endHour: selectedTimeSlot.endHour,
      resourceType: 'employee',
      resourceId: selectedTimeSlot.resourceId,
    };
    handleDialogClose();
    openUnavailabilityDialogForSlot(slot);
  };

  const handleDialogSuccess = () => {
    handleDialogClose();
    handleRefresh();
  };

  // Get current resources based on type
  const resources = selectedResourceType === 'employee' ? employees : serviceBays;
  const selectedResource = resources.find((r) => r.id === selectedResourceId);

  // Work hours of the shown day for the single-column view: a mechanic's own shift, otherwise
  // (a bay, or everyone) the workshop's hours of that weekday. null = day off, undefined = unknown.
  const dayWorkHours = selectedResourceType === 'employee' && selectedResource
    ? (selectedResource.work_hours ? hoursForDate(selectedResource.work_hours, selectedDate) : undefined)
    : hoursForDate(workSchedule.days, selectedDate);

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: 'calc(100vh - 120px)',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
        }}
      >
        <Alert severity="error" sx={{ mb: 2, width: '100%', maxWidth: 400 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {t('Error')}
          </Typography>
          <Typography variant="body2">{error}</Typography>
        </Alert>
        <Button
          variant="contained"
          onClick={handleRefresh}
          startIcon={<RefreshIcon />}
          sx={{ borderRadius: '10px' }}
        >
          {t('Retry')}
        </Button>
      </Box>
    );
  }

  const isInitialLoad = !currentOrganization || (loading && employees.length === 0);

  if (isInitialLoad) {
    return (
      <Box sx={{ p: 2 }}>
        <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 2, mb: 2 }} />
        <Skeleton variant="rectangular" height={48} sx={{ borderRadius: 2, mb: 2 }} />
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 'calc(100vh - 120px)',
        bgcolor: 'background.default',
      }}
    >
      {/* Loading overlay */}
      {loading && (
        <Box
          sx={{
            position: 'fixed',
            top: 64,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(255, 255, 255, 0.7)',
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress size={40} />
        </Box>
      )}

      {/* Header with date navigation and resource selector */}
      <MobileCalendarHeader
        selectedDate={selectedDate}
        onDateChange={handleDateChange}
        onPrevDay={handlePrevDay}
        onNextDay={handleNextDay}
        onToday={handleToday}
        mode={mobileMode}
        onModeChange={handleModeChange}
        monthCursor={monthCursor}
        monthWeeksText={monthData && monthData.month === monthKey ? weekSpanText(monthData.days) : ''}
        onPrevMonth={handlePrevMonth}
        onNextMonth={handleNextMonth}
        selectedResourceId={selectedResourceId}
        selectedResourceType={selectedResourceType}
        onResourceChange={handleResourceChange}
        employees={employees}
        serviceBays={serviceBays}
        unscheduledCount={unscheduledTotalCount}
        onOpenSearch={() => setSearchDialogOpen(true)}
      />

      {/* Google Calendar authorisation expired: one-click reconnect for admins */}
      <GoogleCalendarAuthBanner />

      {/* Month grid, or the day timeline (multi-column when employee/bay with "All" selected) */}
      {isMonthMode && monthError ? (
        <Box
          data-testid="mobile-month-error"
          sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3 }}
        >
          <Alert severity="error" sx={{ mb: 2, width: '100%', maxWidth: 400 }}>
            {t('Failed to load data. Please try again.')}
          </Alert>
          <Button
            variant="contained"
            onClick={handleRefresh}
            startIcon={<RefreshIcon />}
            sx={{ borderRadius: '10px' }}
          >
            {t('Retry')}
          </Button>
        </Box>
      ) : isMonthMode ? (
        <MobileMonthView
          monthData={monthData && monthData.month === monthKey ? monthData : null}
          loading={monthLoading}
          selectedDate={selectedDate}
          onSelectDay={handleMonthDaySelect}
          onJumpToNextFreeDay={handleJumpToNextFreeDay}
          openHours={openHours}
          onOpenHoursChange={handleOpenHoursChange}
        />
      ) : selectedResourceType !== 'all' && !selectedResourceId ? (
        <MobileMultiColumnView
          selectedDate={selectedDate}
          events={getEventsForDay()}
          unavailabilities={unavailabilities}
          calendarNotes={getCalendarNotesForDay()}
          workSchedule={workSchedule}
          startHour={startHour}
          endHour={endHour}
          onTimeSlotTap={handleTimeSlotTap}
          onEventTap={handleEventTap}
          resources={selectedResourceType === 'employee' ? employees : serviceBays}
          resourceType={selectedResourceType}
          serviceBays={serviceBays}
        />
      ) : (
        <MobileDayView
          selectedDate={selectedDate}
          events={getEventsForDay()}
          unavailabilities={getUnavailabilitiesForDay()}
          calendarNotes={getCalendarNotesForDay()}
          workSchedule={workSchedule}
          startHour={startHour}
          endHour={endHour}
          dayWorkHours={dayWorkHours}
          onTimeSlotTap={handleTimeSlotTap}
          onEventTap={handleEventTap}
          selectedResource={selectedResource}
          selectedResourceType={selectedResourceType}
          isAllView={selectedResourceType === 'all'}
          employees={employees}
          serviceBays={serviceBays}
        />
      )}

      {/* Week day selector — shown in multi-column mode */}
      {!isMonthMode && selectedResourceType !== 'all' && !selectedResourceId && (
        <MobileWeekDaySelector
          selectedDate={selectedDate}
          onDateChange={handleDateChange}
        />
      )}

      {/* Mobile Schedule Dialog */}
      <MobileScheduleDialog
        open={mobileDialogOpen}
        mode={mobileDialogMode}
        selectedTimeSlot={selectedTimeSlot}
        selectedEvent={selectedEvent}
        onClose={handleDialogClose}
        onSuccess={handleDialogSuccess}
        onAddNote={selectedTimeSlot && selectedTimeSlot.resourceType !== 'bay' ? handleAddNote : undefined}
        onAddUnavailability={
          selectedTimeSlot?.resourceType === 'employee' && selectedTimeSlot.resourceId
            ? handleAddUnavailability
            : undefined
        }
        employees={employees}
        serviceBays={serviceBays}
        workSchedule={workSchedule}
      />

      {/* Calendar note and absence dialogs (shared with the desktop) */}
      <CalendarNoteDialog />
      <UnavailabilityDialog />

      {/* Work order search */}
      <MobileCalendarSearchDialog
        open={searchDialogOpen}
        onClose={() => setSearchDialogOpen(false)}
        onSelect={handleSearchSelect}
      />
    </Box>
  );
}

export default withTranslation()(MobileCalendarView);
